import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router';

import WatchData from '@shared/sections/WatchData';
import { WatchDataProvider } from '@shared/sections/WatchData/Context';
import theme from '@shared/themes/default';
import Autocomplete from '@shared/atoms/Autocomplete';
import T from '@shared/atoms/Typography';

import { useAppGlobals } from '../../contexts/AppContext';
import { listOrganizationOptions } from '../../../utils';
import { listOrganizations } from './graphql/queries';

const AutocompleteWrapper = styled.div`
  max-width: 400px;
  margin-bottom: 1.5rem;
`;

const WatchDataStatisticsView = () => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const {
    getRestClient,
    useGraphQLMutation,
    useLazyGraphQLQuery,
    useNewGraphQLQuery,
    user,
  } = useAppGlobals();
  const { t } = useTranslation();

  const searchParams = new URLSearchParams(search);
  const preSelectedOrganizationId = searchParams.get('organization');
  const view = searchParams.get('view') || 'users';
  const id = searchParams.get('id');

  const [organization, setOrganization] = useState();
  const [organizationList, setOrganizationList] = useState([]);

  const listOrganizationsResponse = useNewGraphQLQuery(listOrganizations, {
    variables: {
      pagination: { limit: 1000 },
    },
  });

  useEffect(() => {
    const { loading, data } = listOrganizationsResponse;
    if (!loading && data?.listOrganizations) {
      setOrganizationList(data.listOrganizations);
    }
  }, [listOrganizationsResponse]);

  useEffect(() => {
    if (organizationList.length > 0) {
      if (!preSelectedOrganizationId) {
        setOrganization({});
      } else {
        const organization = organizationList.find(
          (organization) => organization.id === preSelectedOrganizationId
        );

        if (organization) {
          setOrganization(organization);
        } else {
          navigate('/activity');
        }

        // TODO: Error handling for the case the organization is not found
      }
    }
  }, [preSelectedOrganizationId, organizationList]);

  const handleHistory = (queryParams) => {
    if (preSelectedOrganizationId) {
      navigate(
        `/activity?organization=${preSelectedOrganizationId}&${queryParams}`
      );
    } else if (queryParams) {
      navigate(`/activity?${queryParams}`);
    } else {
      navigate('/activity');
    }
  };

  const selectOrganizationHandler = (organization) => {
    const { value: organizationId } = organization || {};

    if (organizationId) {
      navigate(
        view
          ? `/activity?organization=${organizationId}&view=${view}`
          : `/activity?organization=${organizationId}`
      );
    } else {
      navigate(
        ['users', 'courses'].includes(view)
          ? `/activity?view=${view}`
          : '/activity?view=users'
      );
    }
  };

  return (
    <div>
      <T
        color={theme.palette.lightPure}
        sx={{
          marginBottom: theme.spacing.m1,
        }}
        variant="subtitle"
      >
        {t('backoffice.general.findOrganization')}
      </T>
      <AutocompleteWrapper>
        <Autocomplete
          filterOptions
          getOptionKey={(option) => option.value}
          getOptionLabel={(option) => option.name}
          loading={listOrganizationsResponse.loading}
          onChange={selectOrganizationHandler}
          options={listOrganizationOptions(organizationList)}
          preSelectedValue={
            organization?.id && {
              name: organization.name
                ? `${organization.archived ? '(Archived) ' : ''}${organization.name}`
                : `No name (ID: ${organization.id})`,
              value: organization.id,
            }
          }
        />
      </AutocompleteWrapper>
      {organization && (
        <WatchDataProvider
          handleHistory={handleHistory}
          id={id}
          isBackOffice
          organizationSelectedId={organization.id}
          restClient={getRestClient()}
          selectOrganizationHandler={selectOrganizationHandler}
          useGraphQLMutation={useGraphQLMutation}
          useLazyGraphQLQuery={useLazyGraphQLQuery}
          user={user}
          view={view}
        >
          <WatchData />
        </WatchDataProvider>
      )}
    </div>
  );
};

export default WatchDataStatisticsView;
